<template>
  <div id="kbo-sidebar" class="kbo-sidebar--container">
    <kbo-sidebar-item
      route="/areasanitariavigo/service"
      icon="fas fa-taxi"
      label="Nuevo servicio"
    />
    <kbo-sidebar-item
      route="/areasanitariavigo/bookings"
      icon="far fa-calendar-alt"
      label="Reservas"
    />
    <kbo-sidebar-item route="/areasanitariavigo/historic" icon="fas fa-list" label="Historial" />
  </div>
</template>

<script>
import KboSidebarItem from "@/layout/components/Sidebar/KboSidebarItem.vue";
import { getUnixTimeInSeconds } from "@/utils/date.js";
import { parse } from "flatted";
import html2canvas from "html2canvas";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "KboSidebar",

  components: {
    KboSidebarItem,
  },

  computed: {
    ...mapGetters("app", ["version", "selectedWorkgroupsIds", "selectedCentralsIds"]),
    ...mapGetters("operators", ["hasAccessTo"]),
    ...mapGetters("persistedCache", ["persistedCachedRoutes"]),
  },

  methods: {
    ...mapActions("persistedCache", ["removePersistedCachedRoute"]),
    handleNewService() {
      this.$router.push(`/services/create/${getUnixTimeInSeconds()}`);
    },
    hasAnyPermission(routes) {
      for (let i = 0; i < routes.length; i++) {
        if (this.hasPermission(routes[i])) return true;
      }
      return false;
    },
    hasPermission(route) {
      return this.hasAccessTo(route);
    },
    escapeRegex(text) {
      return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    },
    serviceIcon(service) {
      const serviceType = this.serviceType(service);
      switch (serviceType) {
        case "now":
          return "fas fa-list";
        case "historic":
          return "fas fa-list";
        case "booking":
          return "far fa-calendar-alt";
        case "periodic":
          return "fas fa-redo";
        default:
          return "fas fa-paper-plane";
      }
    },
    serviceType(service) {
      const entry = this.persistedCachedRoutes?.[service]?.ServiceView;
      if (!entry) {
        return null;
      }
      return parse(entry)?.editingServiceTypeValue || null;
    },
    serviceDetails(service) {
      const entry = this.persistedCachedRoutes?.[service]?.ServiceView;
      if (entry) {
        const data = parse(entry);
        const phone = data.customerData?.phone || "Sin teléfono";
        return `${phone}`;
      }
      return "Sin teléfono";
    },
    editingServiceType(service) {
      const serviceType = this.serviceType(service);
      switch (serviceType) {
        case "historic":
          return "Histórico";
        case "booking":
          return "Reserva";
        case "periodic":
          return "Periódico";
        case "now":
          return "En curso";
        default:
          return serviceType;
      }
    },
    isNewServiceNotEmpty(service, route) {
      return (
        service?.routeData?.from?.street ||
        service?.routeData?.to?.street ||
        service?.scheduleData?.mode !== "now" ||
        service?.propertiesData?.taxi ||
        service?.subscriberData?.subscriber ||
        Object.values(service?.stagesData?.stages).filter((s) => s !== "").length > 0 ||
        (service?.customerData?.id && !route.includes("asteriskId"))
      );
    },
    handleCloseEmptyNewServices() {
      this.cachedCreatingEmptyServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseNotEmptyNewServices() {
      this.cachedCreatingNotEmptyServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseLiveServices() {
      this.cachedEditingLiveServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseBookings() {
      this.cachedEditingBookingServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleClosePeriodics() {
      this.cachedEditingPeriodicServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    handleCloseHistorics() {
      this.cachedEditingHistoricServices.forEach(async (route) => {
        await this.closeIfCurrentRoute(route);
        this.removePersistedCachedRoute(route);
      });
    },
    closeIfCurrentRoute(route) {
      return new Promise((resolve) => {
        if (this.$route.fullPath === route) {
          this.$router.push(
            this.$route.query.fromName ? { name: this.$route.query.fromName } : "/"
          );
          return setTimeout(resolve, 1000);
        }
        resolve();
      });
    },
    handleCreateTicketInContext() {
      html2canvas(document.body).then(async (canvas) => {
        canvas.toBlob(
          async (data) => {
            window.screenshot = data;
            window.ticketContext = {
              time: getUnixTimeInSeconds(),
              version: this.version,
              selectedWorkgroupsIds: this.selectedWorkgroupsIds,
              selectedCentralsIds: this.selectedCentralsIds,
              route: {
                name: this.$route.name,
                fullPath: this.$route.fullPath,
                path: this.$route.path,
                params: this.$route.params,
                query: this.$route.query,
              },
            };
            if (this.$route.path.startsWith("/support")) {
              this.$router.push("/", () => {
                setTimeout(() => {
                  this.$router.push("/support?create=true");
                }, 500);
              });
            } else {
              this.$router.push("/support?create=true");
            }
          },
          "image/jpg",
          0.85
        );
      });
    },
  },
};
</script>

<style lang="scss">
.kbo-sidebar--container.close {
  .kbo-sidebar--submenu .items {
    display: none !important;
  }
}
</style>

<style lang="scss" scoped>
.kbo-sidebar--container {
  position: fixed;
  z-index: 10002;
  top: var(--topbar-height);
  color: white !important;
  background-color: $menuBg2;
  bottom: 0;
  left: 0;
  width: 54px;

  .kbo-sidebar--separator {
    height: 2px;
    background-color: white;
    width: 100%;
  }
}

.close-section {
  color: white;
  cursor: pointer;
  text-decoration: underline;
}

.neositek .kbo-sidebar--container {
  background-color: $neositekMenuBg;
}
</style>
